import { analytics, GCInstant, globalTime } from '@play-co/gcinstant';

import StateObserver from 'src/StateObserver';
import { updateSessionRevenueSequence } from 'src/state/analytics';
import {
  ChannelProperty,
  FirstEntryUserProperties,
  LastEntryUserProperties,
  PayloadProperties,
} from 'src/lib/analytics/properties';
import { duration } from 'src/replicant/utils/duration';
import { PurchaseInfo } from '@play-co/replicant';
import { getLaunchScene } from './stateUtils';

function getPlatform(): { app: string; os: string } {
  const entryPoint = GCInstant.entryPointName;

  const os = GCInstant.osType || 'UNKNOWN';

  let app: string;

  if (os === 'IOS' || os === 'ANDROID') {
    if (/messenger/i.test(navigator.userAgent)) {
      app = 'MESSENGER';
    } else if (/fbav/i.test(navigator.userAgent)) {
      app = 'FACEBOOK';
    } else {
      // 1. Set FACEBOOK for known Facebook entry points
      if (
        entryPoint === 'bookmark' ||
        entryPoint === 'facebook_gaming_tab' ||
        entryPoint === 'facebook_web' ||
        entryPoint === 'feed' ||
        entryPoint === 'group' ||
        entryPoint === 'notification' ||
        entryPoint === 'web_games_hub'
      ) {
        app = 'FACEBOOK';
      }

      // 2. Set MESSENGER for known Messenger entry points
      else if (
        entryPoint === 'game_composer' ||
        entryPoint === 'games_hub' ||
        entryPoint === 'menu' ||
        entryPoint === 'thread_suggestion' ||
        entryPoint === 'video_call'
      ) {
        app = 'MESSENGER';
      }

      // 3. Set UNKNOWN for everything else
      else {
        app = 'UNKNOWN';
      }
    }
  } else if (os === 'IOS_APP') {
    app = 'NATIVE';
  } else {
    if (document.referrer.indexOf('www.messenger.com') !== -1) {
      app = 'MESSENGER';
    } else if (document.referrer.indexOf('.facebook.com') !== -1) {
      app = 'FACEBOOK';
    } else {
      app = 'UNKNOWN';
    }
  }

  return { app, os };
}

function getPurchaseDaysElapsed(
  now: number,
  history: PurchaseInfo[],
): number | undefined {
  if (!history.length) {
    return undefined;
  }

  const delta = now - history[history.length - 1].purchase_time;
  return Math.max(0, delta) / duration({ days: 1 });
}

export async function sendEntryFinalAnalytics(): Promise<void> {
  const now = globalTime.getNow();
  const entryData = GCInstant.entryData;
  const realtimeProps = analytics.getUserProperties();

  const $platform = getPlatform();

  const purchaseHistory = StateObserver.replicant.getPurchaseHistory();

  // EntryFinal event properties
  const entryFinalEventProps: { [key: string]: any } = {
    $subFeature: entryData.$subFeature,

    // TODO: Deprecated, remove after 2020-11-21
    // UpdateAsync
    $sourceUpdatePushExplicit: entryData.$pushExplicit,
    $sourceUpdateStrategy: entryData.$strategy,

    // THUG-945
    $capHoursIndex: entryData.$capHoursIndex,
    $capHoursStart: entryData.$capHoursStart,
    $capHoursEnd: entryData.$capHoursEnd,
    $capHoursAvg: entryData.$capHoursAvg,
    $capSequence: entryData.$capSequence,

    // THUG-964
    $creativeAssetID: entryData.$creativeAssetID,
    $creativeTextID: entryData.$creativeTextID,
    $creativeCTA: entryData.$creativeCTA,

    // Marketing community posts info
    $pageLinkId: entryData.$pageLinkId,
    $pageLinkType: entryData.$pageLinkType,

    // THUG-1052
    sourceUpdatePushConsecutive: entryData.pushConsecutive,

    // THUG-1051
    sourceUpdatePushExpected: entryData.pushExpected,

    isAppSwitch: entryData.isAppSwitch,
  };

  // First session user properties
  const firstEntryUserProps: FirstEntryUserProperties = {
    // TODO Deprecated, remove someday
    $firstEntrySourcePlatformOS: entryData.$platformOS,
    $firstEntrySourcePlatformApp: entryData.$platformApp,
    $firstEntrySourcePlatformAppOS: entryData.$platformAppOS,

    // Neo
    $firstEntrySourceRealtimeIsPurchaser: entryData.$realtimeIsPurchaser,
    $firstEntrySourceRealtimeLTV: entryData.$realtimeLTV,
    $firstEntrySourceRealtimeLocale: entryData.$realtimeLocale,

    // iOS native app
    $firstEntryAppleVersion:
      GCInstant.insideNativeIOS && window.TLNative.Settings.os === 'ios'
        ? window.TLNative.Settings.nativeVersion
        : undefined,
  };

  // Last session user properties
  const lastEntryUserProps: LastEntryUserProperties = {
    // TODO Deprecated, remove someday
    $platformOS: $platform.os,
    $platformApp: $platform.app,
    $platformAppOS: `${$platform.app}_${$platform.os}`,

    // TODO Deprecated, remove someday
    $lastEntrySourcePlatformOS: entryData.$platformOS,
    $lastEntrySourcePlatformApp: entryData.$platformApp,
    $lastEntrySourcePlatformAppOS: entryData.$platformAppOS,

    // Neo
    $lastEntryIsPurchaser: realtimeProps.$realtimeIsPurchaser,
    $lastEntryLTV: realtimeProps.$realtimeLTV,

    $lastEntrySourceRealtimeIsPurchaser: entryData.$realtimeIsPurchaser,
    $lastEntrySourceRealtimeLTV: entryData.$realtimeLTV,
    $lastEntrySourceRealtimeLocale: entryData.$realtimeLocale,

    // iOS native app
    $lastEntryAppleVersion: GCInstant.insideNativeIOS
      ? window.TLNative.Settings.nativeVersion
      : undefined,

    // THUG-1190
    $lastEntryPurchaseDaysElapsed: getPurchaseDaysElapsed(now, purchaseHistory),

    // Entry Scene
    $lastEntryScene: getLaunchScene(),
  };

  // Send off all the props to GCInstant
  return GCInstant.sendEntryFinalAnalytics(
    entryFinalEventProps,
    firstEntryUserProps,
    lastEntryUserProps,
  );
}

export function makePayload($channel: ChannelProperty): PayloadProperties {
  const lastProps = analytics.getUserProperties();

  return {
    $channel: $channel,

    // Last session user properties
    // TODO Deprecated, remove someday
    $platformOS: lastProps.$platformOS,
    $platformApp: lastProps.$platformApp,
    $platformAppOS: lastProps.$platformAppOS,

    // Neo
    $realtimeIsPurchaser: lastProps.$realtimeIsPurchaser,
    $realtimeLTV: lastProps.$realtimeLTV,
    $realtimeLocale: lastProps.$realtimeLocale,
  };
}

export function updateRevenueSequence(
  revenueType: string,
  feature: string,
): void {
  // Update session sequences on redux
  StateObserver.dispatch(
    updateSessionRevenueSequence({ revenueType, feature }),
  );
}
